import moment from 'moment';

/**
 * Returns true when a field value is non-empty.
 */
const hasFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    if (typeof value === 'string') {
        return value.trim() !== '';
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'object') {
        const innerValue = value.value ?? value.name ?? value.label;
        return innerValue !== null && innerValue !== undefined && innerValue !== '';
    }

    return true;
};

/**
 * Evaluates a single configured condition against a row state.
 */
export const evaluateCondition = (row = {}, condition = {}, context = {}) => {
    if (!condition) {
        return false;
    }

    const operator = condition.operator;

    if (operator === 'default') {
        return true;
    }

    if (condition.type === 'relevantTierApprovalDate') {
        const topTierField = context.topTierField || 'topTierRequired';
        const tierApprovalDateFields = context.tierApprovalDateFields || {};
        const topTierVal = row[topTierField];
        const tierMatch = topTierVal == null ? null : String(topTierVal).match(/tier\s*(\d+)/i);
        const tierNumber = tierMatch ? parseInt(tierMatch[1], 10) : null;
        const approvalDateKey = tierNumber != null ? tierApprovalDateFields[tierNumber] : null;

        if (!approvalDateKey) {
            return false;
        }

        const dateValue = row[approvalDateKey];
        if (operator === 'hasValue') {
            return hasFieldValue(dateValue) && moment.parseZone(dateValue).isValid();
        }

        return false;
    }

    const fieldKey = condition.field;
    const rawValue = row[fieldKey];
    const numericValue = Number(rawValue);
    const compareValue = Number(condition.value);

    switch (operator) {
        case 'equals':
            return rawValue === condition.value;
        case 'lt':
            return !Number.isNaN(numericValue) && !Number.isNaN(compareValue) && numericValue < compareValue;
        case 'lte':
            return !Number.isNaN(numericValue) && !Number.isNaN(compareValue) && numericValue <= compareValue;
        case 'gt':
            return !Number.isNaN(numericValue) && !Number.isNaN(compareValue) && numericValue > compareValue;
        case 'gte':
            return !Number.isNaN(numericValue) && !Number.isNaN(compareValue) && numericValue >= compareValue;
        case 'hasValue':
        case 'isThere':
            return hasFieldValue(rawValue);
        case 'anyOf':
            return Array.isArray(condition.value) && condition.value.includes(rawValue);
        default:
            return false;
    }
};

/**
 * Evaluates all conditions in a list (AND logic).
 */
export const evaluateAllConditions = (row = {}, conditions = [], context = {}) => (
    (conditions || []).every((condition) => evaluateCondition(row, condition, context))
);
